/**
 * `qc.*` wire contract (garment Increment 7, `garment.qc`).
 *
 * End-line AQL inspection per lot + the Style File's QC read-model. The sampling
 * plan (ANSI Z1.4 General Level II; majors @ 2.5, minors @ 1.5) and verdict are
 * computed server-side from `lotSize` + the defect lines; the client never sends
 * a verdict. Behind `entitledProcedureFor("garment.qc")`.
 */
import { z } from "zod";
import { uuid, isoDate } from "../_common.js";

export const inspectionStage = z.enum(["inline", "end_line", "final"]);
export const defectSeverity = z.enum(["critical", "major", "minor"]);
export const defectDisposition = z.enum(["rework", "reject", "spot", "accept"]);
export const qcVerdict = z.enum(["accept", "reject"]);
export const pinView = z.enum(["front", "back"]);

const pieces = z.number().int().min(0).max(1_000_000);
const pinCoord = z.number().min(0).max(100);

export const defectInput = z.object({
  defectType: z.string().trim().min(1).max(60),
  zone: z.string().trim().max(32).nullable().optional(),
  severity: defectSeverity,
  qty: pieces,
  disposition: defectDisposition.optional(),
  photoKey: z.string().trim().max(256).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});

export const getForStyleInput = z.object({ styleId: uuid });

/** Position a defect pin on the Visual Style File sketch (ADR-0029). */
export const placeInput = z.object({
  defectId: uuid,
  pinX: pinCoord,
  pinY: pinCoord,
  pinView,
});
export const placeOutput = z.object({ id: uuid });

/** Two-phase defect-photo upload (Phase 3). */
export const defectPhotoContentType = z.enum(["image/jpeg", "image/png", "image/webp"]);
export const requestPhotoUploadInput = z.object({
  contentType: defectPhotoContentType,
  size: z.number().int().positive().max(64 * 1024 * 1024),
});
export const requestPhotoUploadOutput = z.object({ uploadUrl: z.string(), objectKey: z.string() });
export const attachPhotoInput = z.object({ defectId: uuid, objectKey: z.string().trim().min(1).max(256) });
export const attachPhotoOutput = z.object({ id: uuid, photoUrl: z.string().nullable() });

export const recordInput = z.object({
  styleId: uuid,
  lotRef: z.string().trim().min(1).max(40),
  inspectionStage: inspectionStage.optional(),
  lotSize: z.number().int().min(1).max(10_000_000),
  inspectedOn: isoDate,
  defects: z.array(defectInput).max(200),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export const recordOutput = z.object({
  id: uuid,
  verdict: qcVerdict,
  sampleSize: z.number().int().nonnegative(),
  codeLetter: z.string(),
  dhuTenths: z.number().int().nonnegative(),
});

const qcDefect = z.object({
  id: uuid,
  defectType: z.string(),
  zone: z.string().nullable(),
  severity: defectSeverity,
  qty: z.number().int(),
  disposition: defectDisposition,
  photoKey: z.string().nullable(),
  photoUrl: z.string().nullable(),
  notes: z.string().nullable(),
  pinX: z.number().nullable(),
  pinY: z.number().nullable(),
  pinView: pinView.nullable(),
});

const qcLot = z.object({
  id: uuid,
  lotRef: z.string(),
  inspectionStage,
  lotSize: z.number().int(),
  sampleSize: z.number().int(),
  codeLetter: z.string(),
  criticalCount: z.number().int(),
  majorCount: z.number().int(),
  minorCount: z.number().int(),
  majorAc: z.number().int(),
  minorAc: z.number().int(),
  dhuTenths: z.number().int(),
  reworkQty: z.number().int(),
  rejectQty: z.number().int(),
  verdict: qcVerdict,
  inspectedOn: isoDate,
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  defects: z.array(qcDefect),
});

export const qcView = z.object({
  styleId: uuid,
  status: z.string(),
  orderQty: z.number().int().nonnegative(),
  canRecord: z.boolean(),
  lots: z.array(qcLot),
  totalChecked: z.number().int().nonnegative(),
  totalDefects: z.number().int().nonnegative(),
  dhuTenths: z.number().int().nonnegative(),
  rftPercent: z.number().int().min(0).max(100),
  hasUnresolvedRejection: z.boolean(),
});
export const getForStyleOutput = qcView;

export type QcViewWire = z.infer<typeof qcView>;
export type DefectInputWire = z.infer<typeof defectInput>;
